import '../style.css'
import React, { useEffect, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import Button from '../../atoms/Buttons'
import FormInput from '../../atoms/Input/Form-Input'
import UploadIcon from '../../../assets/icons/upload.svg'
import { gql, useMutation, useQuery } from '@apollo/client'
import { BeatLoader } from 'react-spinners'
import { createToast } from '../../../utils/Toast'
import TextArea from '../../atoms/Input/TextArea'
import useSignedUrlUpload from '../../../hooks/useSignedUrlUpload'
import useManufacturerBrandingUpload from '../../../hooks/useManufacturerBrandingUpload'
import { PreviewHeroBanner } from '../../PreviewHeroBanner/PreviewHeroBanner'
// Removed list header usage in favor of user.banner
import { useUserContext } from '../../../context/UserContext'
import { UPDATE_USER } from '../../../apollo/mutations/updateUser'

const Toast = createToast()

const HeroBanner = ({ type }) => {
  const imageRef = useRef(null)
  const logoRef = useRef(null)
  const brand = localStorage.getItem('brand') || ''
  const { userData } = useUserContext()
  const businessForm = JSON?.parse(userData?.manufacturerObject?.businessForm)
  const { uploadFile } = useSignedUrlUpload()
  const { uploadFile: uploadBrandingFile } = useManufacturerBrandingUpload()
  const [updateUser, { loading: updatingUser }] = useMutation(UPDATE_USER)
  console.log({ userData })

  const [imageUrl, setImageUrl] = useState(null)
  const [videoUrl, setVideoUrl] = useState(null)
  const [mediaUpload, setMediaUpload] = useState(false)
  const [error, setError] = useState('')
  const [isEditingLogo, setIsEditingLogo] = useState(false)
  const [logoUrlInput, setLogoUrlInput] = useState(
    businessForm?.branding?.logoUrl || '',
  )
  const [logoUploading, setLogoUploading] = useState(false)

  const methods = useForm({
    defaultValues: {
      title: '',
      description: '',
      button_text: '',
      url: '',
    },
  })

  useEffect(() => {
    const banner = userData?.banner
    setImageUrl(banner?.image || null)
    setVideoUrl(banner?.video || null)
    methods?.setValue('url', banner?.btnUrl || '')
    methods?.setValue('title', banner?.title || '')
    methods?.setValue('description', banner?.description || '')
    methods?.setValue('btnLabel', banner?.btnLabel || '')
  }, [userData?.banner])

  const handleClickImage = () => {
    imageRef.current.click()
  }

  const handleClickLogo = () => {
    logoRef.current?.click()
  }

  const handleImage = async event => {
    const file = event.target.files[0]
    try {
      setMediaUpload(true)
      const url = await uploadFile(file)
      if (file.type?.includes('image')) {
        setImageUrl(url)
        setVideoUrl(null)
      } else {
        setVideoUrl(url)
        setImageUrl(null)
      }
      setMediaUpload(false)
    } catch (error) {
      Toast.fire({
        icon: 'error',
        title: error.message?.toString() || 'Undefined error',
      })
      setMediaUpload(false)
    }
  }

  const handleLogoFileChange = async event => {
    const file = event.target.files?.[0]
    if (!file) return
    try {
      setLogoUploading(true)
      const url = await uploadBrandingFile(file, brand, 'logo')
      setLogoUrlInput(url)
      const formData = {
        ...businessForm,
        branding: {
          ...(businessForm?.branding || {}),
          logoUrl: url,
        },
      }
      await updateUser({
        variables: {
          input: {
            id: userData?.id,
            businessForm: JSON.stringify(formData),
          },
        },
        refetchQueries: ['ClientQuery'],
      })
      Toast.fire({ icon: 'success', title: 'Logo updated successfully' })
      setIsEditingLogo(false)
    } catch (e) {
      console.error('Logo upload failed', e)
      Toast.fire({
        icon: 'error',
        title: e.message?.toString?.() || 'Logo upload failed',
      })
    } finally {
      setLogoUploading(false)
      if (logoRef.current) logoRef.current.value = ''
    }
  }

  const onSubmit = item => {
    try {
      const bannerInput = {
        title: item?.title,
        description: item?.description,
        btnLabel: item?.btnLabel,
        image: imageUrl ? imageUrl : null,
        video: videoUrl ? videoUrl : null,
        btnUrl: item?.url,
      }

      updateUser({
        variables: {
          input: {
            id: userData?.id,
            banner: bannerInput,
          },
        },
        refetchQueries: ['ClientQuery'],
      })

      Toast.fire({
        icon: 'success',
        title: 'Updated Successfully',
      })
    } catch (error) {
      Toast.fire({
        icon: 'error',
        title: error.message?.toString() || 'Undefined error',
      })
    }
  }

  return (
    <div className="py-5">
      <div className="w-full h-auto mt-2 pb-5 mx-auto">
        {/* Preview at top */}
        <FormProvider {...methods}>
          {updatingUser || mediaUpload ? (
            <div className="flex space-x-2 justify-center items-center bg-white h-screen">
              <span className="sr-only">Loading...</span>
              <div className="h-8 w-8 bg-[#2b364b] rounded-full animate-bounce [animation-delay:-0.3s]"></div>
              <div className="h-8 w-8 bg-[#2b364b] rounded-full animate-bounce [animation-delay:-0.15s]"></div>
              <div className="h-8 w-8 bg-[#2b364b] rounded-full animate-bounce"></div>
            </div>
          ) : (
            <PreviewHeroBanner
              logoUrl={logoUrlInput || businessForm?.branding?.logoUrl}
              imageUrl={imageUrl}
              videoUrl={videoUrl}
            />
          )}
        </FormProvider>

        {/* Edit controls below */}
        {/* Hero Media */}
        <div className="mt-6 mb-2">
          <div className="flex items-center justify-between">
            <h3 className="change-font-family text-[#2B364B] text-2xl font-semibold font-barlowcondensed">
              Hero Media
            </h3>
            <span className="text-xs text-gray-500 font-montserrat">
              Image or Video (shown in preview)
            </span>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-4">
          {(() => {
            const hasHeroMedia = Boolean(imageUrl || videoUrl)
            return (
              <div
                onClick={handleClickImage}
                className={`group relative w-[320px] h-[180px] rounded-lg overflow-hidden cursor-pointer ${hasHeroMedia
                    ? 'border border-gray-200 bg-gray-100'
                    : 'border border-gray-300 bg-gray-50'
                  }`}
                title="Upload hero image or video"
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt="Hero media preview"
                    className="w-full h-full object-cover"
                  />
                ) : videoUrl ? (
                  <video
                    src={videoUrl}
                    className="w-full h-full object-cover"
                    preload="metadata"
                    muted
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <img src={UploadIcon} alt="Upload" className="w-8 h-8" />
                  </div>
                )}

                {hasHeroMedia && (
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none border-2 border-dashed border-gray-500">
                    <span className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow">
                      <img src={UploadIcon} alt="Upload" className="w-5 h-5" />
                    </span>
                  </div>
                )}
              </div>
            )
          })()}
        </div>

        <input
          type="file"
          ref={imageRef}
          accept="video/*,image/*"
          onChange={handleImage}
          style={{ display: 'none' }}
        />

        {/* Company Logo uploader */}
        <div className="mt-6">
          <div className="flex items-center justify-between">
            <h3 className="change-font-family text-[#2B364B] text-2xl font-semibold font-barlowcondensed">
              Company Logo
            </h3>
            <span className="text-xs text-gray-500 font-montserrat">
              Logo is displayed in the live preview
            </span>
          </div>
          <div className="mt-3 flex items-center gap-4">
            {(() => {
              const hasLogo = Boolean(
                logoUrlInput || businessForm?.branding?.logoUrl,
              )
              return (
                <div
                  onClick={handleClickLogo}
                  className={`group relative w-[96px] h-[96px] rounded-lg overflow-hidden flex items-center justify-center cursor-pointer ${hasLogo
                      ? 'border-2 border-dashed border-gray-500 px-4'
                      : 'border border-gray-300 bg-gray-50'
                    }`}
                  title="Upload logo"
                >
                  {hasLogo ? (
                    <img
                      src={logoUrlInput || businessForm?.branding?.logoUrl}
                      alt="Logo preview"
                      className="w-full h-full object-contain bg-white"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <img src={UploadIcon} alt="Upload" className="w-6 h-6" />
                    </div>
                  )}

                  {hasLogo && (
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                      <span className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow">
                        <img src={UploadIcon} alt="Upload" className="w-4 h-4" />
                      </span>
                    </div>
                  )}
                </div>
              )
            })()}
            <input
              ref={logoRef}
              type="file"
              accept="image/*"
              onChange={handleLogoFileChange}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        {/* Content */}
        <FormProvider {...methods}>
          <div className="mt-8 mb-2">
            <h3 className="change-font-family text-[#2B364B] text-2xl font-semibold font-barlowcondensed">
              Content
            </h3>
            <span className="text-sm text-gray-500 font-montserrat">
              Update the banner text and button
            </span>
          </div>
          <form onSubmit={methods.handleSubmit(onSubmit)} className="mt-6">
            <div>
              <FormInput
                className="add_saddle_detail-form__field w-[100%] mx-auto mt-4"
                label="Title"
                name="title"
                required
                maxLength={23}
                onChange={e => methods.setValue('title', e.target.value)}
                placeholder="Enter title"
              />
              <div className="!w-[100%] mt-4 mx-auto relative">
                <TextArea
                  placeholder="description"
                  id="description"
                  name="description"
                  maxLength={166}
                  rows="4"
                  validations={{
                    required: {
                      value: true,
                      message: 'Description is required',
                    },
                  }}
                  className="w-[100%] h-[167.582px] rounded-[30px] mx-auto !mt-[31px] lg:mt-[68px] "
                ></TextArea>
                <label className="absolute change-font-family bg-[#fff] px-2 top-[-10px] left-2">
                  Description
                </label>
              </div>
            </div>
            <div>
              <FormInput
                className="add_saddle_detail-form__field w-[250px] mt-4"
                label="Button Text"
                maxLength={12}
                name="btnLabel"
                required
                onChange={e => methods.setValue('btnLabel', e.target.value)}
                placeholder="Enter button text"
              />
            </div>
            <FormInput
              className="add_saddle_detail-form__field w-[100%] mx-auto mt-4"
              label="Url"
              required
              name="url"
              onChange={e => methods.setValue('url', e.target.value)}
              placeholder="Enter URL"
            />
            {error && (
              <div className="error-message" style={{ color: 'red' }}>
                {error}
              </div>
            )}
            <Button
              type={'submit'}
              className="hero-section-save-btn cursor-pointer w-[200px] h-[48px] mt-8 bg-[#2b364b] rounded-full border-none text-[#fff] hover:opacity-90"
            >
              {updatingUser ? <BeatLoader color={'#ffffff'} size={10} /> : 'Save'}
            </Button>
          </form>
        </FormProvider>
      </div>
    </div>
  )
}

export default HeroBanner
